'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/mobile-app/install-and-sign-in.mdx
 * (published at user-docs.totalaccess.co.za/mobile-app/install-and-sign-in)
 */

/**
 * BiometricLockScreen
 * 
 * Full-screen lock overlay that appears when the app resumes from background
 * and biometric unlock is enabled. The user must verify via fingerprint/face
 * before they can access the app.
 * 
 * Flow:
 * 1. App goes to background -> visibilitychange saves timestamp
 * 2. App resumes -> visibilitychange checks if biometric is enabled
 * 3. If yes AND enough time has passed (>5s), shows lock screen
 * 4. User verifies biometrics -> lock screen dismissed
 * 5. If user cancels -> stays locked, can retry or exit
 * 
 * COLD START: This component is mounted at root layout level so it loads
 * BEFORE any routing/redirects, ensuring the lock screen covers the app
 * immediately on cold start.
 * 
 * INCOMING CALLS: Listens for 'totalaccess-incoming-call' custom DOM event
 * dispatched by CallContext. Auto-unlocks so the user can answer the call
 * without needing biometric verification first.
 * 
 * NOTE: The app now uses BOTH document visibility and Capacitor lifecycle events.
 * Some Android lock/unlock flows do not consistently emit only one signal, so the
 * biometric gate listens to both and de-duplicates its checks.
 * 
 * @author TotalAccess Development Team
 * @date February 2026
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import biometricAuth, { BIOMETRIC_ENABLED_EVENT } from '@/plugins/BiometricAuth';
import { isBiometricLockSuppressed, setBiometricLockActive } from '@/lib/biometricLockState';
import LockScreen from '@/plugins/LockScreen';
import type { TotalAccessNativeBridge } from '@/lib/nativeCacheBridge';
import UserDocsLink from '@/components/shared/UserDocsLink';

declare global {
  interface Window {
    TotalAccessNative?: TotalAccessNativeBridge;
  }
}

const LOCK_TIMEOUT_MS = 2000; // Lock after 2 seconds in background
const FOREGROUND_CHECK_DELAY_MS = 160;
const FOREGROUND_CHECK_COOLDOWN_MS = 900;
const AUTO_PROMPT_DELAY_MS = 120;
const BACKGROUND_TIME_KEY = 'biometric_background_time';
const UNLOCK_GRACE_PERIOD_MS = 4000;
const DEVICE_LOCK_POLL_INTERVAL_MS = 3000; // Poll device lock state every 3s

export function BiometricLockScreen() {
  const [isLocked, setIsLocked] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [biometricLabel, setBiometricLabel] = useState('Fingerprint');
  const mountedRef = useRef(true);
  const isLockedRef = useRef(false);
  const verifyingRef = useRef(false);
  const recentlyUnlockedAtRef = useRef<number>(0);
  const biometricEnabledRef = useRef(false);
  const foregroundCheckTimeoutRef = useRef<number | null>(null);
  const pendingForegroundReasonRef = useRef<string | null>(null);
  const lastForegroundCheckAtRef = useRef(0);
  const autoPromptTimeoutRef = useRef<number | null>(null);
  const autoPromptSequenceRef = useRef(0);
  // Track whether the current unlock was a temporary call bypass
  // When true, the lock screen will re-engage once the call ends
  const callBypassRef = useRef(false);
  // Track whether any call (incoming or outgoing) is currently active
  // When true, the lock screen will not engage even if the device is locked
  const onCallRef = useRef(false);
  // Interval handle for periodic device lock state polling
  const deviceLockPollRef = useRef<number | null>(null);

  // Keep refs in sync
  useEffect(() => {
    isLockedRef.current = isLocked;
  }, [isLocked]);

  useEffect(() => {
    verifyingRef.current = verifying;
  }, [verifying]);

  const markRecentlyUnlocked = useCallback(() => {
    const now = Date.now();
    recentlyUnlockedAtRef.current = now;

    try {
      localStorage.removeItem(BACKGROUND_TIME_KEY);
      localStorage.setItem(BACKGROUND_TIME_KEY, now.toString());
    } catch { }
  }, []);

  const isWithinUnlockGracePeriod = useCallback(() => {
    return Date.now() - recentlyUnlockedAtRef.current < UNLOCK_GRACE_PERIOD_MS;
  }, []);

  const clearForegroundCheckTimeout = useCallback(() => {
    if (foregroundCheckTimeoutRef.current !== null) {
      window.clearTimeout(foregroundCheckTimeoutRef.current);
      foregroundCheckTimeoutRef.current = null;
    }

    pendingForegroundReasonRef.current = null;
  }, []);

  const clearAutoPromptTimeout = useCallback(() => {
    if (autoPromptTimeoutRef.current !== null) {
      window.clearTimeout(autoPromptTimeoutRef.current);
      autoPromptTimeoutRef.current = null;
    }

    autoPromptSequenceRef.current += 1;
  }, []);

  const isFingerprintOnlyLockEligible = useCallback(async () => {
    // Simplified: just check if biometrics are available
    // Allow both face and fingerprint - let the native system handle it
    const availability = await biometricAuth.isAvailable();
    return availability.isAvailable;

    return true;
  }, []);

  const runBiometricVerification = useCallback(async () => {
    if (verifyingRef.current || !biometricEnabledRef.current) return false;

    setVerifying(true);
    setError(null);

    try {
      const lockEligible = await isFingerprintOnlyLockEligible();
      if (!lockEligible) {
        biometricEnabledRef.current = false;
        setBiometricLockActive(false);
        setIsLocked(false);
        setError('Biometric unlock requires device biometric security.');
        return false;
      }

      const verified = await biometricAuth.verify({
        reason: 'Unlock TotalAccess',
        title: 'Unlock App',
        subtitle: 'Verify your identity',
        description: 'Use your biometric to continue',
        negativeButtonText: 'Cancel',
        useFallback: false,
        requiredBiometry: 'any',
      });

      if (verified) {
        markRecentlyUnlocked();
        clearForegroundCheckTimeout();
        clearAutoPromptTimeout();
        isLockedRef.current = false;
        setBiometricLockActive(false);
        setIsLocked(false);
        setError(null);
        // Real biometric unlock — clear any call bypass state
        callBypassRef.current = false;
        return true;
      }

      setError('Verification failed. Tap to try again.');
      return false;
    } catch (err) {
      console.error('[BiometricLock] Unlock error:', err);
      setError('Authentication error. Tap to try again.');
      return false;
    } finally {
      if (mountedRef.current) {
        setVerifying(false);
      }
    }
  }, [clearAutoPromptTimeout, clearForegroundCheckTimeout, isFingerprintOnlyLockEligible, markRecentlyUnlocked]);

  /**
   * Attempt biometric verification
   */
  const attemptUnlock = useCallback(() => {
    void runBiometricVerification();
  }, [runBiometricVerification]);

  /**
   * Handle app exit from lock screen
   */
  const handleExit = useCallback(() => {
    try {
      CapacitorApp.exitApp();
    } catch (err) {
      console.error('[BiometricLock] Failed to exit:', err);
    }
  }, []);

  const engageLock = useCallback((reason: string) => {
    if (!mountedRef.current || !biometricEnabledRef.current || callBypassRef.current || onCallRef.current) {
      return false;
    }

    if (isBiometricLockSuppressed()) {
      console.log('[BiometricLock] Lock suppressed — skipping engage:', reason);
      return false;
    }

    if (isWithinUnlockGracePeriod()) {
      console.log('[BiometricLock] Within unlock grace period, skip re-lock');
      return false;
    }

    if (!isLockedRef.current) {
      console.log('[BiometricLock] Engaging biometric lock:', reason);
      isLockedRef.current = true;
      setBiometricLockActive(true);
      setIsLocked(true);
    }

    setError(null);

    clearAutoPromptTimeout();
    const promptSequence = autoPromptSequenceRef.current;

    autoPromptTimeoutRef.current = window.setTimeout(() => {
      autoPromptTimeoutRef.current = null;

      void (async () => {
        await new Promise<void>((resolve) => {
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => resolve());
          });
        });

        if (
          !mountedRef.current
          || promptSequence !== autoPromptSequenceRef.current
          || !biometricEnabledRef.current
          || !isLockedRef.current
          || verifyingRef.current
        ) {
          return;
        }

        if (document.visibilityState !== 'visible') {
          return;
        }

        void runBiometricVerification();
      })();
    }, AUTO_PROMPT_DELAY_MS);

    return true;
  }, [clearAutoPromptTimeout, isWithinUnlockGracePeriod, runBiometricVerification]);

  /**
   * Main effect: listen to both document visibility and Capacitor lifecycle
   * events so Android lock/unlock transitions cannot silently skip the app lock.
   */
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    mountedRef.current = true;
    let isCancelled = false;

    const reconcileNativeLockState = async (reason: string): Promise<boolean> => {
      if (!mountedRef.current || callBypassRef.current || onCallRef.current || !biometricEnabledRef.current) {
        return false;
      }

      if (isBiometricLockSuppressed()) {
        return false;
      }

      if (isWithinUnlockGracePeriod()) {
        return false;
      }

      try {
        const [lockState, overLockState] = await Promise.all([
          LockScreen.isDeviceLocked().catch(() => null),
          LockScreen.isShowingOverLockScreen().catch(() => null),
        ]);

        const deviceLocked = Boolean(lockState?.isLocked);
        const showingOverLockScreen = Boolean(overLockState?.isShowingOverLockScreen);

        if (!deviceLocked && !showingOverLockScreen) {
          return false;
        }

        console.log('[BiometricLock] Native lock-state reconcile:', {
          reason,
          deviceLocked,
          showingOverLockScreen,
        });

        if (deviceLocked && showingOverLockScreen) {
          await LockScreen.finishAndLock().catch((err) => {
            console.warn('[BiometricLock] finishAndLock during reconcile failed:', err);
          });
        }

        if (!isLockedRef.current) {
          return engageLock(`native-${reason}`);
        }

        return false;
      } catch (err) {
        console.error('[BiometricLock] Native lock-state reconcile failed:', err);
        return false;
      }
    };

    const handleForegroundSecurityCheck = async (reason: string) => {
      console.log('[BiometricLock] Foreground security check:', reason);

      if (!mountedRef.current || !biometricEnabledRef.current) {
        return;
      }

      if (isBiometricLockSuppressed()) {
        console.log('[BiometricLock] Foreground check skipped — lock suppression active');
        return;
      }

      if (isWithinUnlockGracePeriod()) {
        console.log('[BiometricLock] Foreground check skipped during unlock grace period');
        return;
      }

      try {
        try {
          const nativeBridge = window.TotalAccessNative;
          if (nativeBridge?.shouldBypassBiometric && nativeBridge.shouldBypassBiometric()) {
            console.log(`[BiometricLock] ${reason} — native shouldBypassBiometric() = true, skipping lock`);
            callBypassRef.current = true;
            return;
          }
        } catch { }

        const reconciled = await reconcileNativeLockState(reason);
        if (reconciled) {
          return;
        }

        const bgTimeStr = localStorage.getItem(BACKGROUND_TIME_KEY);
        if (bgTimeStr) {
          const bgTime = parseInt(bgTimeStr, 10);
          const elapsed = Date.now() - bgTime;

          if (elapsed < LOCK_TIMEOUT_MS) {
            console.log(`[BiometricLock] Short background time (${elapsed}ms), skip lock`);
            return;
          }
        }

        console.log('[BiometricLock] Locking app — background time exceeded threshold');
        engageLock(reason);
      } catch (error) {
        console.error(`[BiometricLock] ${reason} check failed:`, error);
      }
    };

    const scheduleForegroundSecurityCheck = (reason: string) => {
      if (!mountedRef.current || !biometricEnabledRef.current) {
        return;
      }

      if (isBiometricLockSuppressed()) {
        return;
      }

      pendingForegroundReasonRef.current = pendingForegroundReasonRef.current
        ? `${pendingForegroundReasonRef.current}, ${reason}`
        : reason;

      if (foregroundCheckTimeoutRef.current !== null) {
        return;
      }

      foregroundCheckTimeoutRef.current = window.setTimeout(() => {
        foregroundCheckTimeoutRef.current = null;

        const scheduledReason = pendingForegroundReasonRef.current ?? reason;
        pendingForegroundReasonRef.current = null;

        const now = Date.now();
        if (now - lastForegroundCheckAtRef.current < FOREGROUND_CHECK_COOLDOWN_MS) {
          return;
        }

        lastForegroundCheckAtRef.current = now;
        void handleForegroundSecurityCheck(scheduledReason);
      }, FOREGROUND_CHECK_DELAY_MS);
    };

    const syncBiometricConfiguration = async () => {
      try {
        const availability = await biometricAuth.isAvailable();

        if (!mountedRef.current || isCancelled) {
          return { enabled: false, availability };
        }

        setBiometricLabel(biometricAuth.getBiometryLabel(availability.biometryType));

        // Lock-screen biometric unlock is intentionally fingerprint-only.
        // This prevents Android face/device toggles from appearing in unlock mode.
        const fingerprintOnlyEligible = await isFingerprintOnlyLockEligible();
        const nextEnabled = availability.isAvailable
          && fingerprintOnlyEligible
          && biometricAuth.isEnabled();
        biometricEnabledRef.current = nextEnabled;

        if (!nextEnabled) {
          clearForegroundCheckTimeout();
          clearAutoPromptTimeout();
          isLockedRef.current = false;
          callBypassRef.current = false;
          setBiometricLockActive(false);
          setIsLocked(false);
          setVerifying(false);
          setError(null);
        }

        return { enabled: nextEnabled, availability };
      } catch (err) {
        console.error('[BiometricLock] Failed to sync biometric configuration:', err);
        biometricEnabledRef.current = false;
        setBiometricLockActive(false);
        return { enabled: false, availability: { isAvailable: false, biometryType: null as string | null } };
      }
    };

    // COLD START CHECK: On mount, immediately check if biometric is enabled
    // and lock the app before the user sees any content.
    // EXCEPTION: If the app was launched for an incoming call (via FCM notification),
    // skip the lock so the user goes straight to the call UI. The lock will
    // re-engage after the call ends via the callBypassRef mechanism.
    void (async () => {
      try {
        const { enabled } = await syncBiometricConfiguration();

        // If biometric unlock is enabled, lock immediately on cold start
        if (enabled) {
          // Check if app was launched for an incoming call — skip lock if so.
          // We use shouldBypassBiometric() which is a SEPARATE flag from pending_call_answer.
          // This is critical because TotalConnectRTC clears pending_call_answer when it handles
          // the call, but the biometric lock screen may check AFTER that happens (e.g., when
          // the page navigates to /auth/login). The bypass flag survives clearPendingCall().
          let hasPendingCall = false;
          try {
            const nativeBridge = window.TotalAccessNative;
            if (nativeBridge?.shouldBypassBiometric) {
              hasPendingCall = nativeBridge.shouldBypassBiometric();
              if (hasPendingCall) {
                console.log('[BiometricLock] Cold start — native shouldBypassBiometric() = true, skipping lock');
              }
            }
            // Fallback: check getPendingCall (in case old APK doesn't have shouldBypassBiometric)
            if (!hasPendingCall && nativeBridge?.getPendingCall) {
              const pending = nativeBridge.getPendingCall();
              if (pending) {
                hasPendingCall = true;
                console.log('[BiometricLock] Cold start has pending call via native bridge — skipping lock');
              }
            }
            // Fallback: check localStorage (set by native injectPendingCallAnswer)
            if (!hasPendingCall) {
              const localPending = localStorage.getItem('coldstart_pending_call') ||
                localStorage.getItem('totalaccess_pending_call');
              if (localPending) {
                hasPendingCall = true;
                console.log('[BiometricLock] Cold start has pending call via localStorage — skipping lock');
              }
            }
          } catch { }

          if (hasPendingCall) {
            // Mark as call bypass so lock re-engages when call ends
            callBypassRef.current = true;
          } else {
            console.log('[BiometricLock] Cold start — biometric enabled, locking app');
            engageLock('cold-start');
          }
        }
      } catch (err) {
        console.error('[BiometricLock] Cold start check failed:', err);
      }
    })();

    const handleBiometricEnabledChange = () => {
      void syncBiometricConfiguration();
    };

    window.addEventListener(BIOMETRIC_ENABLED_EVENT, handleBiometricEnabledChange as EventListener);

    // VISIBILITY CHECK: handles many background→foreground transitions
    const handleVisibilityChange = async () => {
      if (document.hidden) {
        if (isBiometricLockSuppressed()) {
          console.log('[BiometricLock] Document hidden while suppressed — skipping background timestamp');
          return;
        }

        // App going to background — save timestamp
        console.log('[BiometricLock] Document hidden — saving background timestamp');
        try {
          localStorage.setItem(BACKGROUND_TIME_KEY, Date.now().toString());
        } catch { }
      } else {
        scheduleForegroundSecurityCheck('visibility-visible');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    let appStateChangeSubscription: { remove: () => Promise<void> | void } | null = null;
    let resumeSubscription: { remove: () => Promise<void> | void } | null = null;

    void (async () => {
      try {
        appStateChangeSubscription = await CapacitorApp.addListener('appStateChange', ({ isActive }) => {
          if (!mountedRef.current) {
            return;
          }

          if (!isActive) {
            if (isBiometricLockSuppressed()) {
              console.log('[BiometricLock] appStateChange inactive while suppressed — skipping background timestamp');
              return;
            }

            console.log('[BiometricLock] appStateChange inactive — saving background timestamp');
            try {
              localStorage.setItem(BACKGROUND_TIME_KEY, Date.now().toString());
            } catch { }
            return;
          }

          scheduleForegroundSecurityCheck('appStateChange-active');
        });

        resumeSubscription = await CapacitorApp.addListener('resume', () => {
          if (!mountedRef.current) {
            return;
          }

          scheduleForegroundSecurityCheck('resume');
        });
      } catch (error) {
        console.error('[BiometricLock] Failed to register Capacitor lifecycle listeners:', error);
      }
    })();

    // INCOMING CALL BYPASS: If an incoming call arrives while locked,
    // temporarily unlock so the user can answer. The lock re-engages
    // once the call ends (via 'totalaccess-call-ended' event).
    const handleIncomingCall = async () => {
      let shouldBypassForCall = isLockedRef.current;

      try {
        const [lockState, overLockState] = await Promise.all([
          LockScreen.isDeviceLocked().catch(() => null),
          LockScreen.isShowingOverLockScreen().catch(() => null),
        ]);

        shouldBypassForCall = shouldBypassForCall
          || Boolean(lockState?.isLocked)
          || Boolean(overLockState?.isShowingOverLockScreen);
      } catch (err) {
        console.warn('[BiometricLock] Incoming call lock-state check failed:', err);
      }

      if (!shouldBypassForCall) {
        return;
      }

      console.log('[BiometricLock] Incoming call detected — temporary unlock');
      callBypassRef.current = true;
      markRecentlyUnlocked();
      clearForegroundCheckTimeout();
      clearAutoPromptTimeout();
      isLockedRef.current = false;
      setBiometricLockActive(false);
      setIsLocked(false);
      setError(null);
      setVerifying(false);
    };

    // CALL ENDED: If the unlock was a temporary call bypass, re-lock the app
    // so the user must authenticate before accessing other features.
    const handleCallEnded = async () => {
      onCallRef.current = false;
      if (!callBypassRef.current) return;
      console.log('[BiometricLock] Call ended — re-locking app');
      callBypassRef.current = false;

      if (biometricEnabledRef.current) {
        engageLock('call-ended');
      }
    };

    // ACTIVE CALL BYPASS: If an outgoing call is initiated (or any call becomes
    // active), set the bypass flag so the lock screen doesn't cover the call UI.
    // This complements the incoming-call bypass above and covers outgoing calls.
    const handleCallActive = () => {
      console.log('[BiometricLock] Call active — setting onCall bypass');
      onCallRef.current = true;
      callBypassRef.current = true;
      markRecentlyUnlocked();
      clearForegroundCheckTimeout();
      clearAutoPromptTimeout();
      isLockedRef.current = false;
      setBiometricLockActive(false);
      setIsLocked(false);
      setError(null);
      setVerifying(false);
    };

    // PERIODIC DEVICE LOCK POLLING: Some Android OEMs don't reliably fire
    // appStateChange or visibilitychange when the device locks (e.g., when
    // the screen turns off without the app going fully to background). This
    // poll checks LockScreen.isDeviceLocked() every few seconds while the app
    // is visible and not on a call. If the device is locked, we engage the
    // biometric lock immediately so the user never sees a stale, non-functional
    // page behind the lock screen.
    const startDeviceLockPoll = () => {
      if (deviceLockPollRef.current !== null) return;
      deviceLockPollRef.current = window.setInterval(() => {
        if (!mountedRef.current || !biometricEnabledRef.current || onCallRef.current || callBypassRef.current) {
          return;
        }
        if (document.visibilityState !== 'visible') {
          return;
        }
        if (isBiometricLockSuppressed() || isWithinUnlockGracePeriod()) {
          return;
        }
        if (isLockedRef.current) {
          return;
        }
        void (async () => {
          try {
            const lockState = await LockScreen.isDeviceLocked().catch(() => null);
            if (lockState?.isLocked) {
              console.log('[BiometricLock] Device lock detected via periodic poll — engaging lock');
              engageLock('device-lock-poll');
            }
          } catch { }
        })();
      }, DEVICE_LOCK_POLL_INTERVAL_MS);
    };

    const stopDeviceLockPoll = () => {
      if (deviceLockPollRef.current !== null) {
        window.clearInterval(deviceLockPollRef.current);
        deviceLockPollRef.current = null;
      }
    };

    // ── Phase 13: Remote lock from admin (via FCM data-only push) ──
    // The capacitorDeviceService dispatches this event when it receives a
    // `remote_lock` FCM data message. We engage the biometric lock
    // immediately, bypassing the call-bypass and grace-period guards since
    // this is an admin-initiated security action.
    const handleRemoteLock = (event: Event) => {
      const detail = (event as CustomEvent<{ reason?: string }>).detail;
      console.log('[BiometricLock] Remote lock received:', detail?.reason);
      // Force-engage the lock even if biometric is disabled — the admin
      // explicitly requested it. We call engageLock directly; it will
      // respect the biometricEnabledRef check, so if biometric is off the
      // lock won't engage. This is intentional: remote_lock only applies
      // to devices with biometric lock configured.
      engageLock(`remote-${detail?.reason || 'admin_lock'}`);
    };
    window.addEventListener('remoteLock', handleRemoteLock as EventListener);

    window.addEventListener('totalaccess-incoming-call', handleIncomingCall);
    window.addEventListener('totalaccess-call-ended', handleCallEnded);
    window.addEventListener('totalaccess-call-active', handleCallActive);

    startDeviceLockPoll();
    void reconcileNativeLockState('mount');

    return () => {
      mountedRef.current = false;
      isCancelled = true;
      clearForegroundCheckTimeout();
      clearAutoPromptTimeout();
      stopDeviceLockPoll();
      setBiometricLockActive(false);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      void appStateChangeSubscription?.remove();
      void resumeSubscription?.remove();
      window.removeEventListener(BIOMETRIC_ENABLED_EVENT, handleBiometricEnabledChange as EventListener);
      window.removeEventListener('totalaccess-incoming-call', handleIncomingCall);
      window.removeEventListener('totalaccess-call-ended', handleCallEnded);
      window.removeEventListener('totalaccess-call-active', handleCallActive);
      window.removeEventListener('remoteLock', handleRemoteLock as EventListener);
    };
  }, [clearAutoPromptTimeout, clearForegroundCheckTimeout, engageLock, isFingerprintOnlyLockEligible, isWithinUnlockGracePeriod, markRecentlyUnlocked]);

  // Clear onCallRef when call ends — this is handled in the main effect's
  // handleCallEnded, but we also clear it here as a safety net in case the
  // main effect re-runs and the closure is stale.
  useEffect(() => {
    if (!onCallRef.current) return;
    const handler = () => {
      onCallRef.current = false;
    };
    window.addEventListener('totalaccess-call-ended', handler);
    return () => window.removeEventListener('totalaccess-call-ended', handler);
  }, []);

  // Don't render on web or when unlocked
  if (!isLocked) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex flex-col items-center bg-gradient-to-b from-slate-900 to-slate-800"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {/* Header */}
      <div className="mt-12 text-center">
        <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg">
          <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-white">TotalAccess Locked<UserDocsLink docsPath="/mobile-app/install-and-sign-in#lock-the-app-with-your-fingerprint-or-face" label="User guide: App lock" className="ml-1 align-middle text-white/80 hover:text-white" /></h1>
        <p className="text-sm text-gray-400 mt-2">Use {biometricLabel.toLowerCase()} to unlock</p>
      </div>

      {/* Fingerprint icon / touch area */}
      {!verifying && (
        <button
          onClick={attemptUnlock}
          className="mt-8 w-24 h-24 rounded-full bg-white/10 border-2 border-white/20 flex items-center justify-center active:scale-95 transition-transform"
        >
          <svg className="w-12 h-12 text-teal-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 10V14M8 6C8 6 6 7 6 10V14C6 17 8 18 8 18M16 6C16 6 18 7 18 10V14C18 17 16 18 16 18" />
            <path d="M12 2C12 2 8 4 8 10M12 2C12 2 16 4 16 10M12 2V6" />
            <circle cx="12" cy="12" r="3" />
            <path d="M12 15V22" />
          </svg>
        </button>
      )}

      {/* Status text */}
      <p className="mt-4 text-sm text-gray-400">
        {verifying ? 'Verifying...' : `Tap to unlock with ${biometricLabel.toLowerCase()}`}
      </p>

      {/* Error message */}
      {error && (
        <p className="mt-4 text-sm text-red-400 px-8 text-center">{error}</p>
      )}

      {/* Exit button */}
      <button
        onClick={handleExit}
        className="mt-8 px-6 py-2 text-sm text-gray-500 hover:text-gray-300 transition-colors"
      >
        Exit App
      </button>
    </div>
  );
}

export default BiometricLockScreen;
